import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthGuard } from '../guards/AuthGuard';
import { AuthLayout } from '../layouts/AuthLayout';
import { DashboardLayout } from '../layouts/DashboardLayout';

const page = (loader, exportName) => lazy(() => loader().then((module) => ({ default: module[exportName] })));

const Login = page(() => import('../pages/Login'), 'Login');
const Register = page(() => import('../pages/Register'), 'Register');
const ForgotPassword = page(() => import('../pages/ForgotPassword'), 'ForgotPassword');
const ResetPassword = page(() => import('../pages/ResetPassword'), 'ResetPassword');
const AcceptInvite = page(() => import('../pages/AcceptInvite'), 'AcceptInvite');
const RecoveryCodes = page(() => import('../pages/RecoveryCodes'), 'RecoveryCodes');
const Verification = page(() => import('../pages/Verification'), 'Verification');
const Dashboard = page(() => import('../pages/Dashboard'), 'Dashboard');
const MyTickets = page(() => import('../pages/MyTickets'), 'MyTickets');
const TicketDetails = page(() => import('../pages/TicketDetails'), 'TicketDetails');
const CreateTicket = page(() => import('../pages/CreateTicket'), 'CreateTicket');
const Notifications = page(() => import('../pages/Notifications'), 'Notifications');
const GlobalSearchResults = page(() => import('../pages/GlobalSearchResults'), 'GlobalSearchResults');
const KnowledgeBase = page(() => import('../pages/KnowledgeBase'), 'KnowledgeBase');
const KBArticle = page(() => import('../pages/KBArticle'), 'KBArticle');
const AuditLogs = page(() => import('../pages/AuditLogs'), 'AuditLogs');
const ProfileSettings = page(() => import('../pages/ProfileSettings'), 'ProfileSettings');
const RolePermissionMatrix = page(() => import('../pages/RolePermissionMatrix'), 'RolePermissionMatrix');
const AccessRestricted = page(() => import('../pages/AccessRestricted'), 'AccessRestricted');
const SystemError = page(() => import('../pages/SystemError'), 'SystemError');
const NotFound = page(() => import('../pages/NotFound'), 'NotFound');
const ResourceListPage = page(() => import('../pages/ResourceListPage'), 'ResourceListPage');
const ServicesPage = page(() => import('../pages/ServicesPage'), 'ServicesPage');
const ServiceDetail = page(() => import('../pages/ServiceDetail'), 'ServiceDetail');
const InvoicesPage = page(() => import('../pages/InvoicesPage'), 'InvoicesPage');
const FundRequests = page(() => import('../pages/FundRequests'), 'FundRequests');
const Ledger = page(() => import('../pages/Ledger'), 'Ledger');
const KycPage = page(() => import('../pages/KycPage'), 'KycPage');
const ServiceExpired = page(() => import('../pages/ServiceExpired'), 'ServiceExpired');

const AdminDashboard = page(() => import('../pages/AdminDashboard'), 'AdminDashboard');


export function AppRoutes() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background text-on-background">Loading...</div>}>
    <Routes>
      {/* Public / Guest Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/signup" element={<Navigate to="/register" replace />} />
        <Route path="/org-signup" element={<Navigate to="/register" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/recovery-codes" element={<RecoveryCodes />} />
        <Route path="/verify" element={<Verification />} />
      </Route>

      {/* Authenticated Dashboard Routes */}
      <Route element={<AuthGuard><DashboardLayout /></AuthGuard>}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/tickets" element={<MyTickets />} />
        <Route path="/support-ticket" element={<Navigate to="/tickets" replace />} />
        <Route path="/tickets/new" element={<CreateTicket />} />
        <Route path="/new-ticket" element={<Navigate to="/tickets/new" replace />} />
        <Route path="/tickets/:id" element={<TicketDetails />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/search" element={<GlobalSearchResults />} />
        <Route path="/services" element={<ServicesPage mode="catalog" />} />
        <Route path="/addons-services" element={<Navigate to="/services" replace />} />
        <Route path="/services/:id" element={<ServiceDetail />} />
        <Route path="/admin/services" element={<AuthGuard allowedRoles={['ADMIN']}><ServicesPage mode="addons" admin /></AuthGuard>} />
        <Route path="/addons" element={<AuthGuard allowedRoles={['ADMIN']}><ServicesPage mode="addons" admin /></AuthGuard>} />
        <Route path="/admin/user-services" element={<AuthGuard allowedRoles={['ADMIN']}><ServicesPage mode="user-services" admin /></AuthGuard>} />
        <Route path="/admin/service-requests" element={<AuthGuard allowedRoles={['ADMIN']}><ServicesPage mode="requests" admin /></AuthGuard>} />
        <Route path="/my-services" element={<ServicesPage mode="mine" />} />
        <Route path="/my-addons" element={<ServicesPage mode="mine-addons" />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoice-list" element={<Navigate to="/invoices" replace />} />
        <Route path="/fund-requests" element={<FundRequests />} />
        <Route path="/fund-request" element={<Navigate to="/fund-requests" replace />} />
        <Route path="/ledger" element={<Ledger />} />
        <Route path="/user-ledger" element={<Navigate to="/ledger" replace />} />
        <Route path="/kyc" element={<KycPage />} />
        <Route path="/admin/kyc" element={<AuthGuard allowedRoles={['ADMIN']}><KycPage admin /></AuthGuard>} />
        <Route path="/admin/fund-requests" element={<AuthGuard allowedRoles={['ADMIN']}><FundRequests /></AuthGuard>} />
        <Route path="/admin/ledger" element={<AuthGuard allowedRoles={['ADMIN']}><Ledger /></AuthGuard>} />
        <Route path="/admin/invoices" element={<AuthGuard allowedRoles={['ADMIN']}><InvoicesPage admin /></AuthGuard>} />
        
        {/* Info & Logs */}
        <Route path="/kb" element={<KnowledgeBase />} />
        <Route path="/kb/:id" element={<KBArticle />} />
        <Route path="/audit-logs" element={<AuditLogs />} />
        
        {/* Settings & Admin Controls */}
        <Route path="/settings" element={<ProfileSettings />} />

        <Route path="/permissions" element={<RolePermissionMatrix />} />
        <Route path="/users" element={<AuthGuard allowedRoles={['ADMIN']}><ResourceListPage resource="users" /></AuthGuard>} />
        <Route path="/developers" element={<AuthGuard allowedRoles={['ADMIN']}><ResourceListPage resource="developers" /></AuthGuard>} />
        <Route path="/admin/departments" element={<Navigate to="/admin/designations" replace />} />
        <Route path="/admin/designations" element={<AuthGuard allowedRoles={['ADMIN']}><ResourceListPage resource="designations" /></AuthGuard>} />
        <Route path="/email-logs" element={<AuthGuard allowedRoles={['ADMIN']}><ResourceListPage resource="emailLogs" /></AuthGuard>} />

        {/* Restricted Admin panels */}
        <Route path="/admin" element={<AuthGuard allowedRoles={['ADMIN']}><AdminDashboard /></AuthGuard>} />
      </Route>

      {/* Fallback Views */}
      <Route path="/service-expired" element={<AuthGuard><ServiceExpired /></AuthGuard>} />
      <Route path="/403" element={<AccessRestricted />} />
      <Route path="/404" element={<NotFound />} />
      <Route path="/500" element={<SystemError />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}
